import {
  cn,
  filterByInvoicingFeatures,
  SIDEBAR_LINKS,
  useBreakpoints,
  useInvoicingConfig,
} from '@jobcost-monorepo/utils';
import { useState, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Divider, LogoImage, useAuth } from '../../..';
import LogoImageMini from '../../../atoms/Logo/LogoImageMini';
import Card, { CardProps } from '../SidebarMenuItem';
import OpenIcon from '../openIcon';
import '../styles.css';
import { SubListProps } from '../subListCard';

interface Props {
  setSidebarWidth: (width: number) => void;
}

const DesktopSidebar: React.FC<Props> = ({ setSidebarWidth }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const { config: invoicingConfig } = useInvoicingConfig();
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname.split('/')[1];
  const { isSmall } = useBreakpoints();
  const [isOpenSidebar, setIsOpenSidebar] = useState<boolean>(true);
  const [openedSubList, setOpenedSubList] = useState<number | undefined>(
    undefined,
  );
  const sidebarRef = useRef<HTMLDivElement>(null);

  const filterLinks = (links: CardProps[], isAdmin: boolean): CardProps[] => {
    if (isAdmin) {
      return links;
    }
    return links
      .filter((link) => !link.adminOnly)
      .map((link) => ({
        ...link,
        subList: link.subList ? filterSubList(link.subList) : undefined,
      }))
      .filter((link) => link.subList === undefined || link.subList.length > 0);
  };

  const filterSubList = (subList: SubListProps[]): SubListProps[] => {
    return subList.filter((subLink) => !subLink.adminOnly);
  };

  useEffect(() => {
    if (sidebarRef.current) {
      setSidebarWidth(sidebarRef.current.offsetWidth);
    }
  }, [isOpenSidebar, sidebarRef.current?.offsetWidth]);

  return (
    <div
      ref={sidebarRef}
      className={cn(
        'h-[100%] flex-col shadow-lg shadow-purple50 dark:shadow-extraDark50 rounded-br-3xl bg-white dark:bg-darkGrey',
        isOpenSidebar ? 'w-[260px] p-6' : 'w-[78px] px-3 py-6',
        isSmall ? 'hidden' : 'flex',
      )}
    >
      {isOpenSidebar ? (
        <LogoImage className="w-full fill-black dark:fill-white" />
      ) : (
        <LogoImageMini isGradient className="w-[full]" />
      )}
      <div className="relative">
        <Divider />
        <button
          className={cn(
            'absolute h-full bottom-0 flex items-center justify-center',
            isOpenSidebar ? 'w-12 -right-12' : 'w-6 -right-6',
          )}
          onClick={() => {
            if (isOpenSidebar) setOpenedSubList(undefined);
            setIsOpenSidebar(!isOpenSidebar);
          }}
        >
          <OpenIcon isOpen={isOpenSidebar} />
        </button>
      </div>
      <div
        className={cn(
          'flex flex-1 flex-col space-y-3 overflow-y-auto scrollbar-left',
          isOpenSidebar ? '-ml-3 -mr-6' : '-mx-3',
        )}
        style={{ scrollbarGutter: isOpenSidebar ? 'stable' : 'auto' }}
      >
        {filterLinks(
          filterByInvoicingFeatures(SIDEBAR_LINKS, invoicingConfig?.features),
          isAdmin,
        ).map((item, index) => (
          <Card
            key={`${item.label}_${index}`}
            iconName={item.iconName}
            label={item.label}
            onClick={
              item.navigateTo
                ? () => navigate(item.navigateTo as string)
                : () => {
                    if (openedSubList === index)
                      return setOpenedSubList(undefined);
                    setOpenedSubList(index);
                  }
            }
            subList={item.subList}
            isOpenSidebar={isOpenSidebar}
            setIsOpenSidebar={() => setIsOpenSidebar(true)}
            isOpenSublist={index === openedSubList}
            isSelected={item.navigateTo === path}
          />
        ))}
      </div>
    </div>
  );
};

export default DesktopSidebar;
